import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { ResourceLink } from "@/types/db";
import {
  ExternalLink,
  Search,
  ShoppingBag,
  Calculator,
  Truck,
  Mail,
  Users,
  Snowflake,
  Globe,
  Wrench,
  Link as LinkIcon,
  FileText,
  type LucideIcon,
} from "lucide-react";

interface DriveFolder {
  folder_id: string;
  name: string;
  visible_to_roles: string[];
}

interface DriveStatus {
  connected: boolean;
  master_folder_name: string | null;
  master_folder_id: string | null;
}

// Small curated map of icon names → lucide icons.
// Allows admins to pick familiar names; emoji icon_name values fall through to emoji rendering.
const ICON_MAP: Record<string, LucideIcon> = {
  "external-link": ExternalLink,
  link: LinkIcon,
  globe: Globe,
  "shopping-bag": ShoppingBag,
  shopify: ShoppingBag,
  calculator: Calculator,
  quickbooks: Calculator,
  truck: Truck,
  veeqo: Truck,
  mail: Mail,
  klaviyo: Mail,
  users: Users,
  ghl: Users,
  snowflake: Snowflake,
  "social-snowball": Snowflake,
  wrench: Wrench,
  tool: Wrench,
  file: FileText,
};

function getHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function isEmoji(s: string | null | undefined): boolean {
  if (!s) return false;
  // crude but effective: contains non-ASCII characters
  return /[^\x00-\x7F]/.test(s);
}

function ResourceIcon({ link }: { link: ResourceLink }) {
  const name = link.icon_name?.trim();
  if (name && !isEmoji(name)) {
    const Icon = ICON_MAP[name.toLowerCase()];
    if (Icon) return <Icon className="h-5 w-5 text-teal-dark" strokeWidth={2} />;
  }
  if (name && isEmoji(name)) {
    return <span className="text-2xl leading-none">{name}</span>;
  }
  return <ExternalLink className="h-5 w-5 text-teal-dark" strokeWidth={2} />;
}

function ResourceCard({ link }: { link: ResourceLink }) {
  const host = getHost(link.url);
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer"
      className="group flex items-start gap-3 rounded-xl ring-1 ring-line bg-white p-4 transition hover:shadow-soft hover:ring-teal-dark"
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-mint/30">
        <ResourceIcon link={link} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate font-semibold">{link.title}</div>
          <span className="flex-shrink-0 text-xs text-gray2 group-hover:text-teal-dark">
            Open <span aria-hidden>↗</span>
          </span>
        </div>
        {link.description && (
          <div className="mt-0.5 line-clamp-2 text-xs text-gray2">{link.description}</div>
        )}
        {host && (
          <div className="mt-1 truncate text-[11px] uppercase tracking-wide text-gray2/80">
            {host}
          </div>
        )}
      </div>
    </a>
  );
}

function DriveCard({ folder }: { folder: DriveFolder }) {
  return (
    <a
      href={`https://drive.google.com/drive/folders/${folder.folder_id}`}
      target="_blank"
      rel="noreferrer"
      className="group flex items-start gap-3 rounded-xl ring-1 ring-line bg-white p-4 transition hover:shadow-soft hover:ring-teal-dark"
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-mint/30">
        <span className="text-2xl leading-none">📁</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate font-semibold">{folder.name}</div>
          <span className="flex-shrink-0 text-xs text-gray2 group-hover:text-teal-dark">
            Open <span aria-hidden>↗</span>
          </span>
        </div>
        <div className="mt-1 truncate text-[11px] uppercase tracking-wide text-gray2/80">
          drive.google.com
        </div>
      </div>
    </a>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  "External Sites": "🔗 External Sites",
  Tools: "🛠 Tools",
  "Google Drive": "📁 Google Drive",
};

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat;
}

export function Resources() {
  const [links, setLinks] = useState<ResourceLink[]>([]);
  const [driveFolders, setDriveFolders] = useState<DriveFolder[]>([]);
  const [driveStatus, setDriveStatus] = useState<DriveStatus | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    void load();
  }, []);
  async function load() {
    const [{ data: l }, { data: f }, { data: s }] = await Promise.all([
      supabase.from("hq_resource_links").select("*").order("category").order("sort_order"),
      supabase.from("hq_drive_folders").select("*").order("name"),
      supabase.from("hq_drive_status").select("*").maybeSingle(),
    ]);
    setLinks((l as ResourceLink[]) ?? []);
    setDriveFolders((f as DriveFolder[]) ?? []);
    setDriveStatus((s as DriveStatus) ?? null);
  }

  const q = search.trim().toLowerCase();
  const searchActive = q.length > 0;

  const filteredLinks = useMemo(() => {
    if (!searchActive) return links;
    return links.filter((l) => {
      const hay = [l.title, l.description ?? "", l.url, l.category ?? ""]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [links, q, searchActive]);

  const filteredDriveFolders = useMemo(() => {
    const sorted = [...driveFolders].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
    if (!searchActive) return sorted;
    return sorted.filter((f) => {
      const hay = `${f.name} google drive folder drive.google.com`.toLowerCase();
      return hay.includes(q);
    });
  }, [driveFolders, q, searchActive]);

  const pinned = filteredLinks.filter((l) => l.pinned);
  const unpinned = filteredLinks.filter((l) => !l.pinned);

  // Group unpinned by category, items sorted by sort_order then title
  const grouped: Record<string, ResourceLink[]> = {};
  for (const link of unpinned) {
    const k = link.category || "Other";
    (grouped[k] = grouped[k] || []).push(link);
  }
  for (const k of Object.keys(grouped)) {
    grouped[k].sort((a, b) => {
      const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (so !== 0) return so;
      return a.title.localeCompare(b.title);
    });
  }
  const categoryOrder = Object.keys(grouped).sort((a, b) => {
    // Keep "External Sites" and "Tools" up top in a sensible order
    const priority: Record<string, number> = { "External Sites": 0, Tools: 1 };
    const pa = priority[a] ?? 10;
    const pb = priority[b] ?? 10;
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });

  const nothingVisible =
    filteredLinks.length === 0 && filteredDriveFolders.length === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray2">
          Curated tools, sites, and shared Drive folders. Pinned items show first.
          Drive folders below are pulled from your connected Google Drive — admins
          manage which roles see which folders.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search resources by title, description, URL, or category…"
          className="input pl-9"
        />
      </div>

      {nothingVisible ? (
        searchActive ? (
          <div className="card py-10 text-center text-sm text-gray2">
            No matches for "{search}".
          </div>
        ) : (
          <div className="card py-10 text-center text-sm text-gray2">
            No resources for your role yet. Ask your manager.
          </div>
        )
      ) : searchActive ? (
        /* Flat list when searching */
        <div className="card">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredLinks.map((l) => (
              <ResourceCard key={l.id} link={l} />
            ))}
            {filteredDriveFolders.map((f) => (
              <DriveCard key={f.folder_id} folder={f} />
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Pinned */}
          {pinned.length > 0 && (
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold">📌 Pinned</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pinned
                  .sort((a, b) => {
                    const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
                    if (so !== 0) return so;
                    return a.title.localeCompare(b.title);
                  })
                  .map((l) => (
                    <ResourceCard key={l.id} link={l} />
                  ))}
              </div>
            </div>
          )}

          {/* Grouped categories */}
          {categoryOrder.map((cat) => (
            <div key={cat} className="card">
              <h2 className="mb-4 text-lg font-semibold">{categoryLabel(cat)}</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[cat].map((l) => (
                  <ResourceCard key={l.id} link={l} />
                ))}
              </div>
            </div>
          ))}

          {/* Google Drive folders */}
          {driveStatus?.connected && filteredDriveFolders.length > 0 && (
            <div className="card">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">📁 Google Drive</h2>
                  <p className="text-xs text-gray2">
                    {driveStatus.master_folder_name} · Click a folder to open it in Google Drive.
                  </p>
                </div>
                {driveStatus.master_folder_id && (
                  <a
                    href={`https://drive.google.com/drive/folders/${driveStatus.master_folder_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-teal-dark hover:underline"
                  >
                    Open root ↗
                  </a>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredDriveFolders.map((f) => (
                  <DriveCard key={f.folder_id} folder={f} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
